<script setup lang="ts">
  import { computed, inject, onBeforeMount, provide, ref, watch } from 'vue';
  import { useAuth0 } from '@auth0/auth0-vue';
  import { useUrlSearchParams } from '@vueuse/core';

  import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner.vue';
  import LoginDialog from '@/components/LoginDialog/LoginDialog.vue';
  import LoginOptions from '@/components/LoginOptions/LoginOptions.vue';
  import LoginSso from '@/components/LoginSso/LoginSso.vue';
  import { LoginTypeEnum, type LoginTypeEnum as LoginTypeEnumType } from '@/types/enums';
  import { ChangeLoginTypeKey } from '@/types/symbols';
  import { EnvironmentalConfigKey } from '@/utils/environmental-config';

  const { isAuthenticated, checkSession, isLoading, user } = useAuth0();
  const loginType = ref<LoginTypeEnumType>(LoginTypeEnum.EMAIL);
  const params = useUrlSearchParams('history');
  const redirectUrl = ref<string | undefined>();
  const redirectCookieName = 'gls-redirect-url';
  const slideDirection = ref<'left' | 'right' | ''>('');
  const environmentalConfig = inject(EnvironmentalConfigKey);

  const dialogTitle = computed(() => {
    return isAuthenticated.value === true ? 'Select an Application' : 'Log in to Continue';
  });

  const changeLoginType = (ltype: LoginTypeEnum) => {
    slideDirection.value = ltype === LoginTypeEnum.EMAIL ? 'right' : 'left';
    loginType.value = ltype;
  };

  provide(ChangeLoginTypeKey, changeLoginType);

  const removeRedirectUrlCookie = () => {
    document.cookie = `${redirectCookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  };

  const showDebug = () => {
    return import.meta.env.VITE_SHOW_DEBUG_HTML && import.meta.env.VITE_SHOW_DEBUG_HTML === 'true';
  };

  watch(
    () => isAuthenticated.value,
    () => {
      // check for previously set redirect url, and redirect if it exists and passed validation
      const redirectUrlCookie = document.cookie
        .split(';')
        .find((cookie) => cookie.trim().startsWith(`${redirectCookieName}=`));

      if (redirectUrlCookie && isAuthenticated.value) {
        try {
          const url = redirectUrlCookie.split('=')[1];
          const isValidRedirect = environmentalConfig?.urlWhitelist.some((whitelistedUrl) =>
            url.startsWith(whitelistedUrl),
          );
          if (!isValidRedirect) {
            throw new Error('Invalid Redirect URL:' + url);
          }
          redirectUrl.value = url.toString();
          removeRedirectUrlCookie();
          document.location.href = url.toString();
        } catch (error: any) {
          console.error(error?.message || 'Invalid Redirect URL');
          removeRedirectUrlCookie();
        }
      }
    },
  );

  onBeforeMount(async () => {
    await checkSession();

    // return to url
    if (params.rurl) {
      try {
        const url = params.rurl as string;

        // only use urls that are whitelisted
        const isValidRurl = environmentalConfig?.urlWhitelist.some((whitelistedUrl) => url.startsWith(whitelistedUrl));
        if (!isValidRurl) {
          throw new Error('Invalid rURL: ' + url);
        }

        const cookieValue = `${redirectCookieName}=${params.rurl}; path=/; expires=${new Date(Date.now() + 300000).toUTCString()}; secure; SameSite=Lax`;
        document.cookie = cookieValue;
      } catch (error: any) {
        console.log('error', error);
        console.error(error?.message || 'Invalid rURL');
      }
    }
  });
</script>

<template>
  <div class="login-page">
    <div class="login-page__brand">
      <svg
        width="162"
        height="26"
        viewBox="0 0 162 26"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g id="Logo Group">
          <path
            id="Full Logo"
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M87.7779 14.5227H82.2557C81.9927 14.5227 81.7876 14.2756 81.8519 14.0185C82.057 13.2021 82.3915 12.5365 82.854 12.0194C83.4084 11.403 84.124 11.0924 85.0023 11.0924C85.9032 11.0924 86.6422 11.403 87.2192 12.0194C87.705 12.5415 88.0275 13.215 88.1866 14.0413C88.2347 14.292 88.0303 14.5227 87.7779 14.5227ZM92.1219 9.29041C91.3475 8.34551 90.366 7.59345 89.1773 7.0328C87.9865 6.47358 86.5842 6.19361 84.9676 6.19361C83.5809 6.19361 82.3109 6.45143 81.1561 6.96352C80.0013 7.47704 79.0021 8.17696 78.1585 9.06329C77.3156 9.94962 76.6629 11.0002 76.2011 12.2115C75.7386 13.4257 75.5088 14.7212 75.5088 16.0968V16.1668C75.5088 17.6359 75.7563 18.9793 76.2534 20.1914C76.7499 21.4048 77.4422 22.4361 78.3325 23.2874C79.2206 24.1395 80.2778 24.8044 81.5026 25.2837C82.726 25.7607 84.0781 26 85.556 26C87.4271 26 89.026 25.65 90.3547 24.9501C91.5569 24.3166 92.5844 23.4938 93.4365 22.4782C93.5843 22.3018 93.5553 22.0319 93.3792 21.884L90.3427 19.3414C90.1815 19.2072 89.9481 19.2215 89.7954 19.365C89.2148 19.9092 88.6392 20.3128 88.0685 20.5756C87.4094 20.8784 86.6874 21.0298 85.9032 21.0298C84.863 21.0298 83.9911 20.7741 83.286 20.2606C82.7154 19.8457 82.2925 19.2536 82.0174 18.4851C81.9191 18.2109 82.1178 17.9166 82.4063 17.9166H94.1373C94.4293 17.9123 94.4265 17.4502 94.4265 17.2167V16.551C94.4265 15.1512 94.2355 13.8285 93.8537 12.58C93.4732 11.3316 92.8955 10.2367 92.1219 9.29041ZM119.961 6.19361C118.575 6.19361 117.386 6.46786 116.392 7.01637C115.555 7.47918 114.783 8.08554 114.076 8.8376C113.89 9.03615 113.565 9.03187 113.418 8.80118C112.938 8.04769 112.312 7.44704 111.542 6.9978C110.618 6.46143 109.531 6.19361 108.285 6.19361C106.968 6.19361 105.859 6.49643 104.958 7.10493C104.383 7.49132 103.869 7.91627 103.416 8.37979C103.154 8.64834 102.707 8.47193 102.707 8.09482V7.02994C102.707 6.79997 102.522 6.61356 102.294 6.61356H96.5366C96.3082 6.61356 96.1236 6.79997 96.1236 7.02994V25.1287C96.1236 25.3586 96.3082 25.5458 96.5366 25.5458H102.294C102.522 25.5458 102.707 25.3586 102.707 25.1287V15.2576C102.707 14.2077 102.955 13.42 103.452 12.8957C103.948 12.3694 104.588 12.1072 105.375 12.1072C106.16 12.1072 106.783 12.3694 107.246 12.8957C107.707 13.42 107.939 14.2077 107.939 15.2576V25.1287C107.939 25.3586 108.123 25.5458 108.352 25.5458H114.109C114.337 25.5458 114.522 25.3586 114.522 25.1287V15.2576C114.522 14.2077 114.775 13.42 115.283 12.8957C115.791 12.3694 116.439 12.1072 117.224 12.1072C118.009 12.1072 118.633 12.3694 119.095 12.8957C119.556 13.42 119.788 14.2077 119.788 15.2576V25.1287C119.788 25.3586 119.973 25.5458 120.201 25.5458H125.958C126.186 25.5458 126.371 25.3586 126.371 25.1287V13.1228C126.371 10.9059 125.804 9.19827 124.673 7.99484C123.541 6.79568 121.971 6.19361 119.961 6.19361ZM161.093 6.28931C159.734 6.29359 158.631 6.6407 157.779 7.33205C157.226 7.77915 156.743 8.32194 156.331 8.96116C156.108 9.30898 155.577 9.15257 155.577 8.73833V7.02994C155.577 6.79997 155.393 6.61356 155.164 6.61356H149.407C149.18 6.61356 148.994 6.79997 148.994 7.02994V25.1287C148.994 25.3586 149.18 25.5458 149.407 25.5458H155.164C155.393 25.5458 155.577 25.3586 155.577 25.1287V19.6671C155.577 17.4745 156.04 15.8633 156.963 14.8377C157.888 13.8106 159.218 13.2964 160.948 13.2964H161.504V6.70998C161.504 6.48143 161.319 6.2886 161.093 6.28931ZM140.282 14.5227H134.76C134.498 14.5227 134.292 14.2756 134.356 14.0185C134.562 13.2021 134.896 12.5365 135.358 12.0194C135.912 11.403 136.628 11.0924 137.507 11.0924C138.408 11.0924 139.146 11.403 139.724 12.0194C140.21 12.5415 140.532 13.215 140.691 14.0413C140.74 14.292 140.535 14.5227 140.282 14.5227ZM144.626 9.29041C143.852 8.34551 142.871 7.59345 141.682 7.0328C140.491 6.47358 139.089 6.19361 137.472 6.19361C136.085 6.19361 134.815 6.45143 133.661 6.96352C132.505 7.47704 131.506 8.17696 130.664 9.06329C129.82 9.94962 129.167 11.0002 128.706 12.2115C128.244 13.4257 128.013 14.7212 128.013 16.0968V16.1668C128.013 17.6359 128.261 18.9793 128.759 20.1914C129.254 21.4048 129.947 22.4361 130.837 23.2874C131.725 24.1395 132.782 24.8044 134.008 25.2837C135.23 25.7607 136.583 26 138.061 26C139.932 26 141.531 25.65 142.859 24.9501C144.061 24.3166 145.089 23.4938 145.941 22.4782C146.089 22.3018 146.06 22.0319 145.884 21.884L142.847 19.3414C142.686 19.2072 142.453 19.2215 142.3 19.365C141.719 19.9092 141.144 20.3128 140.573 20.5756C139.915 20.8784 139.192 21.0298 138.408 21.0298C137.367 21.0298 136.496 20.7741 135.791 20.2606C135.22 19.8457 134.797 19.2536 134.522 18.4851C134.424 18.2109 134.622 17.9166 134.911 17.9166H146.642C146.934 17.9123 146.931 17.4502 146.931 17.2167V16.551C146.931 15.1512 146.74 13.8285 146.358 12.58C145.977 11.3316 145.399 10.2367 144.626 9.29041ZM49.6462 18.6222C49.4779 18.4987 49.246 18.528 49.1024 18.6808C48.6527 19.1593 48.198 19.545 47.7397 19.8407C47.1973 20.1914 46.5326 20.367 45.7477 20.367C45.1933 20.367 44.6856 20.2563 44.2231 20.0371C43.7613 19.8164 43.3681 19.515 43.0457 19.135C42.7218 18.7515 42.4672 18.3016 42.2827 17.7795C42.0981 17.2595 42.0062 16.6989 42.0062 16.0968V16.0275C42.0062 15.4476 42.0981 14.9119 42.2827 14.4141C42.4672 13.917 42.7218 13.4771 43.0457 13.0957C43.3681 12.7129 43.7557 12.4122 44.2061 12.1937C44.6566 11.9723 45.1466 11.8616 45.6784 11.8616C46.4874 11.8616 47.1563 12.038 47.6881 12.3879C48.1251 12.6758 48.5395 13.0586 48.9299 13.5371C49.0713 13.7106 49.3195 13.7506 49.4977 13.6164L53.3312 10.7267C53.5087 10.5931 53.5546 10.3382 53.4295 10.1546C52.653 9.01901 51.675 8.09697 50.4941 7.39133C49.2014 6.61713 47.5842 6.2286 45.6437 6.2286C44.1658 6.2286 42.8088 6.49072 41.5727 7.01637C40.3373 7.5406 39.2801 8.25266 38.4025 9.15114C37.5242 10.0482 36.8383 11.0981 36.3412 12.3001C35.8441 13.5014 35.5966 14.7662 35.5966 16.0968V16.1668C35.5966 17.4974 35.8441 18.7622 36.3412 19.9635C36.8383 21.1648 37.5193 22.209 38.3856 23.0953C39.2518 23.9817 40.2843 24.688 41.4864 25.2122C42.6871 25.7379 43.9925 26 45.4012 26C47.4569 26 49.1484 25.5943 50.4771 24.7844C51.6856 24.0481 52.699 23.1117 53.515 21.9762C53.6487 21.7897 53.602 21.5248 53.4181 21.3898L49.6462 18.6222ZM13.5314 0.777772C13.3786 0.474948 12.9501 0.474948 12.7959 0.777772L0.549494 24.978C0.409479 25.2551 0.608895 25.5843 0.917212 25.5843H7.3643C7.5227 25.5843 7.66696 25.4929 7.73626 25.3494L12.7924 14.7862C12.9423 14.4727 13.385 14.4727 13.5356 14.7862L18.5917 25.3494C18.661 25.4929 18.8053 25.5843 18.9637 25.5843H25.4101C25.7191 25.5843 25.9185 25.2551 25.7778 24.978L13.5314 0.777772ZM33.106 0H27.3477C27.1199 0 26.9347 0.186408 26.9347 0.416383V25.1287C26.9347 25.3586 27.1199 25.5458 27.3477 25.5458H33.106C33.3337 25.5458 33.5189 25.3586 33.5189 25.1287V0.416383C33.5189 0.186408 33.3337 0 33.106 0ZM67.5378 6.19361C66.1978 6.19361 65.0769 6.49643 64.1767 7.10493C63.6011 7.49132 63.087 7.91627 62.6344 8.37979C62.3721 8.64834 61.9245 8.47193 61.9245 8.09482V0.416383C61.9245 0.186408 61.7399 0 61.5115 0H55.7546C55.5269 0 55.3416 0.186408 55.3416 0.416383V25.1287C55.3416 25.3586 55.5269 25.5458 55.7546 25.5458H61.5115C61.7399 25.5458 61.9245 25.3586 61.9245 25.1287V15.2926C61.9245 14.242 62.184 13.4507 62.7037 12.9122C63.2235 12.3758 63.8875 12.1072 64.6965 12.1072C65.4814 12.1072 66.1171 12.3758 66.6023 12.9122C67.0867 13.4507 67.3299 14.242 67.3299 15.2926V25.1287C67.3299 25.3586 67.5145 25.5458 67.7429 25.5458H73.5005C73.7282 25.5458 73.9128 25.3586 73.9128 25.1287V13.1571C73.9128 10.9874 73.3527 9.28469 72.2326 8.0484C71.1117 6.81211 69.5475 6.19361 67.5378 6.19361Z"
            fill="#F7941D"
          />
          <path
            id="A"
            d="M12.7881 0.760321C12.9423 0.457497 13.3708 0.457497 13.5235 0.760321L25.77 24.9605C25.9107 25.2376 25.7113 25.5669 25.4023 25.5669H18.9559C18.7975 25.5669 18.6532 25.4755 18.5839 25.3319L13.5278 14.7688C13.3772 14.4552 12.9345 14.4552 12.7846 14.7688L7.72845 25.3319C7.65915 25.4755 7.51489 25.5669 7.35649 25.5669H0.9094C0.601083 25.5669 0.401666 25.2376 0.541682 24.9605L12.7881 0.760321Z"
            fill="#F7941D"
          />
        </g>
      </svg>
    </div>
    <div class="login-page__title">{{ dialogTitle }}</div>
    <div class="login-page__content">
      <LoadingSpinner v-if="isLoading" />
      <Transition :name="slideDirection">
        <section v-if="!isLoading">
          <section v-if="isAuthenticated">
            <LoginOptions />
          </section>
          <section v-else>
            <LoginDialog v-if="loginType === LoginTypeEnum.EMAIL" />
            <LoginSso v-else-if="loginType === LoginTypeEnum.SSO" />
          </section>
        </section>
      </Transition>
    </div>
  </div>
  <div
    v-if="showDebug()"
    class="login-page__debug"
  >
    <pre>
      whiteListedDomains: {{ environmentalConfig?.urlWhitelist }}
      --------------------------------------
      isAuthenticated: {{ isAuthenticated }}
      --------------------------------------
      user: {{ user }}
      --------------------------------------
      Env Config: {{ environmentalConfig?.datacenters }}
    </pre>
  </div>
</template>

<style lang="scss" scoped>
  .login-page {
    z-index: 2;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    background-color: var(--alc-white);
    width: 460px;
    min-height: 520px;
    overflow: hidden;
    position: relative;

    &__brand {
      display: flex;
      justify-content: center;
    }

    &__title {
      color: var(--alc-base-800);
      text-align: center;
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 35px;
    }

    &__content {
      width: 100%;
      position: relative;
    }

    &__form-label {
      display: flex;
      justify-content: space-between;
    }

    &__form-items {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__form-items-provider {
      margin-top: 2rem;
    }

    &__form-items-provider-buttons {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
    }
    &__debug {
      position: absolute;
      background-color: #ccc;
      overflow: auto;
      height: 90vh;
      width: 500px;
      left: 0;
      bottom: 25px;
      z-index: 1;
      display: none;
    }

    @media (max-width: 780px) {
      &__form-items-provider-buttons {
        flex-direction: column;
      }
    }
  }
  @media (max-width: 780px) {
    .login-page {
      width: 100%;
    }
  }
  .left-enter-active,
  .left-leave-active,
  .right-enter-active,
  .right-leave-active {
    transition: 500ms ease-out;
  }

  .left-enter-from {
    position: absolute;
    opacity: 0;
    right: -100%;
  }

  .left-enter-to,
  .left-leave-from {
    position: absolute;
    right: 0;
  }

  .left-leave-to {
    position: absolute;
    right: 100%;
  }

  .right-enter-from {
    position: absolute;
    left: -100%;
    opacity: 0;
  }

  .right-enter-to,
  .right-leave-from {
    position: absolute;
    left: 0;
  }

  .right-leave-to {
    position: absolute;
    left: 100%;
  }
</style>
